import { useParams } from 'react-router-dom';

import { MinistryRow } from '../components/MinistryRow';
import { useCoordination, useMinistriesWithLeaders } from '../hooks/useOrg';
import { useAuth } from '@/app/providers/AuthProvider';
import { Breadcrumb } from '@/shared/components/Breadcrumb';
import { EmptyState } from '@/shared/components/EmptyState';
import { LoadingState } from '@/shared/components/LoadingState';
import { PageHeader } from '@/shared/components/PageHeader';
import { messages } from '@/shared/constants/messages';
import { can } from '@/shared/constants/roles';
import { toUserMessage } from '@/shared/lib/errors';

/**
 * A estrutura de uma coordenação: o que ela faz e quais ministérios respondem
 * a ela, com a liderança vigente de cada um.
 *
 * ⚠️ **Esta tela deixou de ser um painel.** Ela trazia os `StatusTiles` da
 * coordenação inteira, as pills em cada linha de ministério e a listagem
 * "Projetos da coordenação" com filtro — ou seja, a US-11 do PRD inteira. Os
 * três saíram, e a razão é a mesma da grade de coordenações (ação 6 da
 * [ADR-012](../../../../docs/adr/ADR-012-navegacao-por-caso-de-uso.md)): a aba
 * Coordenações é o **diretório** da igreja, e diretório que também mostra
 * atividade acaba respondendo as duas perguntas pela metade. Pior, os números
 * chegavam recortados pela `projects_select`, então o "panorama consolidado"
 * era consolidado do ponto de vista de quem olha, não da coordenação.
 *
 * **Ninguém perde acompanhamento.** Quem tem esta tela como área própria — o
 * coordenador — tem `/projetos` e o Calendário no menu, e os dois mostram o
 * mesmo recorte com a moldura certa; `/projetos` inclusive filtra por
 * ministério, que era o que o `<Select>` daqui fazia.
 */
export function CoordinationDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { currentUser } = useAuth();

  const coordinationQuery = useCoordination(id);
  const ministriesQuery = useMinistriesWithLeaders(id);

  const error = coordinationQuery.error ?? ministriesQuery.error;
  if (error) {
    return <EmptyState title="Não foi possível carregar" description={toUserMessage(error)} />;
  }

  const ministries = ministriesQuery.data;
  if (coordinationQuery.isPending || !ministries) {
    return <LoadingState label="Carregando a coordenação…" />;
  }

  // Coordenação inexistente e coordenação fora do alcance chegam aqui do mesmo
  // jeito — a RLS não distingue as duas, e a tela também não deve.
  const coordination = coordinationQuery.data;
  if (!coordination) {
    return (
      <EmptyState title="Coordenação não encontrada" description={messages.errors.notFound} />
    );
  }

  return (
    <div className="space-y-8">
      <PageHeader
        /**
         * **Sem trilha para o coordenador, e isso é a forma certa e não a
         * ausência de uma.** Com a aba Coordenações fora do menu dele, esta tela
         * deixou de ser um degrau de um caminho e virou a aba dele — o item
         * "Minha área" aponta direto para cá. Uma trilha de um degrau só,
         * repetindo o título logo abaixo, ofereceria "voltar" para a própria
         * página.
         *
         * Para o overseer nada muda: ele chega pela grade de coordenações, e é
         * de lá que ele volta.
         */
        breadcrumb={
          can(currentUser?.roles ?? [], 'readOwnBranch') ? (
            <Breadcrumb
              items={[
                { label: 'Coordenações', to: '/coordenacoes' },
                { label: coordination.name },
              ]}
            />
          ) : undefined
        }
        title={coordination.name}
        /* A descrição cadastrada quando existe; o texto genérico quando não —
           estrutura antiga entrou sem descrição, e isso não é pendência. O
           genérico fala de estrutura, e não do que os ministérios planejam:
           planejamento não mora mais nesta tela. */
        description={
          coordination.description ??
          'Os ministérios desta coordenação e quem responde por cada um.'
        }
      />

      <section className="space-y-3">
        {/* O número vive dentro do <h2> em peso e cor secundários: ele qualifica
            o rótulo ("Ministérios (4)"), não é um dado à parte. Fora do título
            viraria mais uma linha; com o mesmo peso do título, competiria com
            ele. Zero também aparece — a seção existe e está vazia, e isso é
            informação. */}
        <h2 className="text-lg font-semibold">
          Ministérios{' '}
          <span className="tabular text-base font-normal text-muted-foreground">
            ({ministries.length})
          </span>
        </h2>
        {ministries.length === 0 ? (
          <EmptyState title={messages.empty.ministries} />
        ) : (
          <ul className="stagger divide-y divide-border overflow-hidden rounded-lg border border-border bg-card">
            {ministries.map((ministry) => (
              <MinistryRow
                key={ministry.ministry_id}
                id={ministry.ministry_id}
                name={ministry.ministry_name}
                leaderName={ministry.leader_name}
                description={ministry.ministry_description}
                accent
              />
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
